import { useGetQuery, useFindByQuery } from '../../graphql';
import { WorkspaceRoutine } from '../../routine';
import { useEmbeddedRoutines } from './useEmbeddedRoutines';
import { useWorkspaceAsset } from '../../assets/hooks/useWorkspaceAsset';
import { useMemo } from 'react';

export const useRoutineAlerts = (
  workspaceAssetId: string,
  workspaceRoutineId: string,
  productId?: string
) => {
  const { data: workspaceAssetData, isLoading: isWorkspaceAssetIdLoading } =
    useGetQuery({
      modelName: 'workspaceasset',
      query: GET_WORKSPACE_ASSET_FOR_ASSET_ID,
      id: workspaceAssetId,
    });

  const assetId = (workspaceAssetData as any)?.asset?.id;

  const { workspaceAsset: wsAsset, isLoading: isWorkspaceAssetLoading } =
    useWorkspaceAsset(assetId || '', assetId ? false : true);

  const { data: workspaceRoutineData, isLoading: isWorkspaceRoutineLoading } =
    useGetQuery({
      modelName: 'workspaceroutine',
      query: GET_WORKSPACE_ROUTINE,
      id: workspaceRoutineId,
    });

  const { data: routineWorkflowData, isLoading: isWorkflowLoading } =
    useFindByQuery({
      modelName: 'workflow',
      query: FIND_WORKFLOWS,
      variables: { filter: { workspaceAsset: { eq: workspaceAssetId } } },
    });

  // Merge all routines
  const allRoutines = useMemo(() => {
    const routines = [
      ...(wsAsset?.routines || []),
      ...(wsAsset?.groups?.flatMap((g: any) => g.routines || []) || []),
      ...(wsAsset?.asset?.product?.routines || []),
    ];

    const seen = new Set<string>();
    return routines.filter((r) => r?.id && !seen.has(r.id) && seen.add(r.id));
  }, [wsAsset]);

  const { routinesWithEmbedded, isEmbeddedRoutinesLoading } =
    useEmbeddedRoutines(allRoutines);

  const routineWorkflows = useMemo(() => {
    return (routineWorkflowData?.items ?? []).filter(
      (workflow: any) =>
        workflow?.trigger?.config?.workspaceRoutineId === workspaceRoutineId
    );
  }, [routineWorkflowData?.items, workspaceRoutineId]);

  const isLoading =
    isWorkspaceAssetIdLoading ||
    isWorkspaceAssetLoading ||
    isWorkspaceRoutineLoading ||
    isWorkflowLoading ||
    isEmbeddedRoutinesLoading;

  const workflowState = (workspaceAssetData as any)?.workflowState;

  const memoizedWorkspaceAsset = useMemo(() => {
    if (!wsAsset) return undefined;
    return {
      ...wsAsset,
      routines: routinesWithEmbedded,
      ...(workflowState ? { workflowState } : {}),
    };
  }, [wsAsset, routinesWithEmbedded, workflowState]);

  return {
    workspaceAsset: memoizedWorkspaceAsset,
    workspaceRoutine: workspaceRoutineData as WorkspaceRoutine,
    routineWorkflows,
    isLoading,
  };
};

const GET_WORKSPACE_ASSET_FOR_ASSET_ID = `
  query getWorkspaceAsset($id: ID!) {
    getWorkspaceAsset(id: $id) {
      id
      asset {
        id
      }
      workflowState {
        workflowId
        triggerState
        actionState
      }
    }
  }
`;

const GET_WORKSPACE_ROUTINE = `
  query getWorkspaceRoutine($id: ID!) {
    getWorkspaceRoutine(id: $id) {
      id
      name
      workspace {
        id
      }
      type
      config
    }
  }
`;

const FIND_WORKFLOWS = `
  query FindWorkflows($filter: WorkflowFilter) {
    findWorkflows(filter: $filter) {
      items {
        id
        name
        trigger {
          type
          config
        }
        action {
          type
          config
        }
        disabled
        disabledBetween {
          start
          end
        }
        createdAt
        updatedAt
        workspace {
          id
        }
      }
    }
}
`;
